/* ==========================================================================
   Kit Configurator — Phase 1
   Vanilla JS, no external dependencies. Scoped to each [data-kit-configurator]
   root so multiple instances on a page stay independent.

   Responsibilities:
     - read serialised item data (data-kit-data JSON)
     - manage kept/dropped state, persisted in localStorage
     - lock the anchor item, exclude out-of-stock items
     - keep preview, list rows, totals, and CTA in sync
     - announce total changes via an aria-live region
     - submit kept items to the cart and redirect to /cart
   ========================================================================== */
(function () {
  'use strict';

  var MONEY_FALLBACK = function (cents) {
    return '$' + (cents / 100).toFixed(2);
  };

  // Use the theme's money format if Shopify's helper is present, else fall back.
  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(cents, window.Shopify.money_format);
      }
    } catch (e) { /* fall through */ }
    return MONEY_FALLBACK(cents);
  }

  // Escape a string for safe insertion via innerHTML (the discount label is
  // merchant-controlled section copy, but we escape it defensively).
  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function KitConfigurator(root) {
    this.root = root;
    this.handle = root.getAttribute('data-page-handle') || 'kit';
    this.storageKey = 'hh_kit_' + this.handle;

    // Pre-order mode (Phase 2). When on, the cart is stamped with attributes
    // so the order carries a paper-trail of what was disclosed at purchase.
    // When off, preorderAttrs is null and no attributes are ever set.
    this.preorder = root.getAttribute('data-preorder') === 'true';
    this.preorderAttrs = this.preorder ? {
      'Pre-order': 'Yes',
      'Batch': root.getAttribute('data-preorder-batch') || '',
      'Ship Date': root.getAttribute('data-preorder-ship') || '',
      'Cutoff Date': root.getAttribute('data-preorder-cutoff') || ''
    } : null;

    // Host pricing (Phase 3). Off unless the section enables it AND the page
    // is loaded with ?host=<code> matching the configured host code. Public
    // visitors (no/invalid param) never enter host mode — display + checkout
    // are identical to the standard flow. hostPercent drives the on-page
    // discount math; hostCode is auto-applied at checkout via /discount/CODE.
    this.hostEnabled = root.getAttribute('data-host-enabled') === 'true';
    this.hostCode = (root.getAttribute('data-host-code') || '').trim();
    this.hostPercent = parseFloat(root.getAttribute('data-host-percent')) || 0;
    this.hostNote = root.getAttribute('data-host-note') || '';
    // Discount label shown next to the discounted total. [percent] is
    // replaced by the host discount %. (We avoid {{ }} because Shopify's
    // Theme Editor treats it as a dynamic-source binding and errors.) The
    // old {{ percent }} form is still honored for any saved values.
    this.hostDiscountLabel = (root.getAttribute('data-host-discount-label') || '')
      .replace(/\[\s*percent\s*\]/gi, String(this.hostPercent))
      .replace(/\{\{\s*percent\s*\}\}/g, String(this.hostPercent));
    this.designMode = root.getAttribute('data-design-mode') === 'true';
    this.hostMode = false;
    if (this.hostEnabled) {
      // In the Theme Editor, show host mode whenever host pricing is enabled
      // (no ?host= needed) so the merchant can preview/style it. On the live
      // storefront, require a ?host=<code> match.
      if (this.designMode) {
        this.hostMode = true;
      } else if (this.hostCode) {
        var paramCode = '';
        try {
          paramCode = (new URLSearchParams(window.location.search).get('host') || '').trim();
        } catch (e) { paramCode = ''; }
        if (paramCode && paramCode.toLowerCase() === this.hostCode.toLowerCase()) {
          this.hostMode = true;
        }
      }
    }

    var dataEl = root.querySelector('[data-kit-data]');
    this.items = [];
    if (dataEl) {
      try {
        var parsed = JSON.parse(dataEl.textContent);
        this.items = (parsed && parsed.items) || [];
      } catch (e) {
        console.error('[kit-configurator] could not parse item data', e);
      }
    }

    // kept state keyed by block_id. Anchor + available default to kept.
    this.kept = {};
    this.items.forEach(function (it) {
      this.kept[it.block_id] = it.available; // OOS items start un-kept and stay so
    }, this);

    // Chosen variant per block (colour/size picker). Starts at the variant
    // Liquid rendered (selected_or_first_available_variant).
    this.variant = {};
    this.items.forEach(function (it) {
      this.variant[it.block_id] = it.variant_id;
    }, this);

    this.previewCollapsed = false;

    this.restore();
    this.cacheNodes();
    this.bind();
    this.render();
  }

  KitConfigurator.prototype.cacheNodes = function () {
    var r = this.root;
    this.listRows = Array.prototype.slice.call(r.querySelectorAll('[data-kit-row]'));
    this.previewItems = Array.prototype.slice.call(r.querySelectorAll('[data-kit-preview-item]'));
    this.ctaButtons = Array.prototype.slice.call(r.querySelectorAll('[data-kit-cta]'));
    this.hintEl = r.querySelector('[data-kit-hint]');
    this.errorEl = r.querySelector('[data-kit-error]');
    this.countEl = r.querySelector('[data-kit-count]');
    this.totalCountEl = r.querySelector('[data-kit-total-count]');
    this.totalEl = r.querySelector('[data-kit-total]');
    this.hostNoteEl = r.querySelector('[data-kit-host-note]');
    this.previewToggle = r.querySelector('[data-kit-preview-toggle]');
    this.previewEl = r.querySelector('[data-kit-preview]');
    this.previewWrap = r.querySelector('[data-kit-preview-wrap]');
    this.previewCountEl = r.querySelector('[data-kit-preview-count]');
    this.previewTotalEl = r.querySelector('[data-kit-preview-total]');
  };

  KitConfigurator.prototype.bind = function () {
    var self = this;

    this.listRows.forEach(function (row) {
      // Variant picker (bound before the toggle guard: the anchor row has
      // no toggle but may still have options).
      var sel = row.querySelector('[data-kit-variant]');
      if (sel) {
        sel.addEventListener('change', function () {
          var id = row.getAttribute('data-block-id');
          self.variant[id] = sel.value;
          self.persist();
          self.render();
        });
      }

      var toggle = row.querySelector('[data-kit-toggle]');
      if (!toggle) return; // anchor / OOS rows have no toggle
      toggle.addEventListener('click', function () {
        var id = row.getAttribute('data-block-id');
        self.kept[id] = !self.kept[id];
        // TODO(analytics): fire toggle event here — { block_id: id, kept: self.kept[id] }
        self.persist();
        self.render();
      });
    });

    this.ctaButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        var action = btn.getAttribute('data-kit-action') === 'checkout' ? 'checkout' : 'cart';
        self.submit(action);
      });
    });

    if (this.previewToggle) {
      this.previewToggle.addEventListener('click', function () {
        self.previewCollapsed = !self.previewCollapsed;
        self.persist();
        self.renderPreviewCollapse();
      });
    }
  };

  KitConfigurator.prototype.lookup = function (blockId) {
    for (var i = 0; i < this.items.length; i++) {
      if (String(this.items[i].block_id) === String(blockId)) return this.items[i];
    }
    return null;
  };

  // The variant that goes in the cart for an item: the shopper's pick if
  // it is one of the item's variants, else the Liquid default.
  KitConfigurator.prototype.selectedVariant = function (it) {
    var want = String(this.variant[it.block_id] || it.variant_id);
    var list = it.variants || [];
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === want) return list[i];
    }
    return { id: it.variant_id, title: '', price_cents: it.price_cents, available: it.available, image_url: '' };
  };

  // Items that count toward the kit: kept AND available. Anchor always counts.
  KitConfigurator.prototype.keptItems = function () {
    var self = this;
    return this.items.filter(function (it) {
      if (!it.available) return false;
      if (it.is_anchor) return true;
      return !!self.kept[it.block_id];
    });
  };

  KitConfigurator.prototype.optionalKeptCount = function () {
    var self = this;
    return this.items.filter(function (it) {
      return it.available && !it.is_anchor && self.kept[it.block_id];
    }).length;
  };

  KitConfigurator.prototype.render = function () {
    var self = this;
    var kept = this.keptItems();
    var totalCents = kept.reduce(function (sum, it) { return sum + (self.selectedVariant(it).price_cents || 0); }, 0);
    var totalAvailable = this.items.filter(function (it) { return it.available; }).length;

    // Rows + preview items
    this.listRows.forEach(function (row) {
      var id = row.getAttribute('data-block-id');
      var it = self.lookup(id);
      if (!it) return;
      var isKept = it.is_anchor ? true : (it.available && !!self.kept[id]);
      row.classList.toggle('is-dropped', !isKept && !it.is_anchor && it.available);
      var toggle = row.querySelector('[data-kit-toggle]');
      if (toggle) toggle.setAttribute('aria-pressed', isKept ? 'true' : 'false');

      // Reflect the chosen variant: price, picker value, thumb, data attr.
      var v = self.selectedVariant(it);
      var priceOut = row.querySelector('[data-kit-row-price]');
      if (priceOut && it.available) priceOut.textContent = formatMoney(v.price_cents || 0);
      var sel = row.querySelector('[data-kit-variant]');
      if (sel) {
        if (String(sel.value) !== String(v.id)) sel.value = String(v.id);
        sel.disabled = !isKept;
      }
      var thumb = row.querySelector('img.kit-configurator__thumb');
      if (thumb) {
        var src = v.image_url || thumb.getAttribute('data-default-src');
        if (src && thumb.getAttribute('src') !== src) thumb.setAttribute('src', src);
      }
      row.setAttribute('data-variant-id', v.id);
    });

    this.previewItems.forEach(function (pi) {
      var id = pi.getAttribute('data-block-id');
      var it = self.lookup(id);
      if (!it) return;
      var isKept = it.is_anchor ? true : (it.available && !!self.kept[id]);
      pi.classList.toggle('is-dropped', !isKept);
    });

    // Totals. In host mode, show the discounted total with the original
    // struck through; otherwise show the plain total.
    var keptCount = kept.length;
    if (this.countEl) this.countEl.textContent = keptCount;
    if (this.previewCountEl) this.previewCountEl.textContent = keptCount;
    if (this.totalCountEl) this.totalCountEl.textContent = totalAvailable;

    if (this.hostMode && this.hostPercent > 0) {
      var discountedCents = Math.round(totalCents * (1 - this.hostPercent / 100));
      var totalHtml = '<span class="kit-configurator__price-was">' + formatMoney(totalCents) +
        '</span> <span class="kit-configurator__price-now">' + formatMoney(discountedCents) + '</span>';
      if (this.hostDiscountLabel) {
        totalHtml += ' <span class="kit-configurator__price-label">' + escapeHtml(this.hostDiscountLabel) + '</span>';
      }
      if (this.totalEl) this.totalEl.innerHTML = totalHtml;
      if (this.previewTotalEl) this.previewTotalEl.innerHTML = totalHtml;
    } else {
      if (this.totalEl) this.totalEl.textContent = formatMoney(totalCents);
      if (this.previewTotalEl) this.previewTotalEl.textContent = formatMoney(totalCents);
    }

    // Reveal the host note once, when in host mode.
    if (this.hostNoteEl) this.hostNoteEl.hidden = !this.hostMode;

    // Below-minimum: anchor + 0 optional kept
    var belowMin = this.optionalKeptCount() === 0;
    this.ctaButtons.forEach(function (btn) {
      btn.setAttribute('aria-disabled', belowMin ? 'true' : 'false');
    });
    if (this.hintEl) this.hintEl.hidden = !belowMin;

    this.renderPreviewCollapse();
  };

  KitConfigurator.prototype.renderPreviewCollapse = function () {
    if (!this.previewWrap) return;
    this.previewWrap.classList.toggle('is-collapsed', this.previewCollapsed);
    if (this.previewToggle) {
      this.previewToggle.setAttribute('aria-expanded', this.previewCollapsed ? 'false' : 'true');
    }
  };

  // action: 'cart'     → add kit (additive), go to /cart. Keeps any
  //                       items already in the cart.
  //         'checkout' → first remove every line item belonging to THIS
  //                       kit (all kit variants, regardless of whether
  //                       they were originally in the cart), then add the
  //                       current selection, then go to /checkout. This
  //                       guarantees re-editing never doubles kit items
  //                       while preserving unrelated cart items.
  KitConfigurator.prototype.submit = function (action) {
    var self = this;
    var kept = this.keptItems();
    if (!kept.length) return;

    if (this.errorEl) this.errorEl.hidden = true;
    this.setLoading(true);

    var work;
    if (action === 'checkout') {
      work = this.removeKitVariantsFromCart().then(function () {
        return self.addKept(kept);
      });
    } else {
      work = this.addKept(kept);
    }

    // Stamp pre-order cart attributes after the kit is in the cart (no-op when
    // pre-order is off). Non-fatal: a failed attributes call must never block
    // the add or the redirect.
    work = work.then(function () {
      return self.setCartAttributes();
    });

    work
      .then(function () {
        self.clearState();
        // Notify the theme (header count, cart drawer) that the cart changed.
        try {
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'kit-configurator' });
          }
        } catch (e) { /* non-fatal */ }
        self.redirectAfterAdd(action);
      })
      .catch(function (err) {
        console.error('[kit-configurator]', err);
        if (self.errorEl) self.errorEl.hidden = false;
        self.setLoading(false);
      });
  };

  KitConfigurator.prototype.setLoading = function (on) {
    this.ctaButtons.forEach(function (btn) {
      if (on) {
        btn.setAttribute('aria-disabled', 'true');
        btn.classList.add('is-loading');
      } else {
        btn.setAttribute('aria-disabled', 'false');
        btn.classList.remove('is-loading');
      }
    });
  };

  KitConfigurator.prototype.addKept = function (kept) {
    var self = this;
    var addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';
    return fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: kept.map(function (it) { return { id: self.selectedVariant(it).id, quantity: 1 }; })
      })
    }).then(function (res) {
      if (!res.ok) throw new Error('cart add failed: ' + res.status);
      return res.json();
    });
  };

  // Remove every variant that belongs to this kit from the cart, using
  // /cart/update.js with quantity 0. Uses the full kit variant list (not
  // just kept items) so any previously-added kit lines are cleared. No-op
  // if the kit has no variants. Unrelated cart items are untouched.
  KitConfigurator.prototype.removeKitVariantsFromCart = function () {
    var updates = {};
    this.items.forEach(function (it) {
      if (it.variant_id) updates[it.variant_id] = 0;
      // Every colour/size of every kit product, so a re-edit that switched
      // variants never leaves the old one behind.
      (it.variants || []).forEach(function (v) { if (v.id) updates[v.id] = 0; });
    });
    if (!Object.keys(updates).length) return Promise.resolve();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (res) {
      // Non-fatal: if the update fails we still try to add. Worst case is
      // a possible duplicate, which is better than blocking the purchase.
      if (!res.ok) console.warn('[kit-configurator] cart pre-clear failed: ' + res.status);
      return true;
    });
  };

  // Stamp pre-order cart attributes (Pre-order / Batch / Ship Date / Cutoff
  // Date) via /cart/update.js. No-op when pre-order is off. Non-fatal — a
  // failure is logged but resolves so the add/redirect still completes.
  KitConfigurator.prototype.setCartAttributes = function () {
    if (!this.preorderAttrs) return Promise.resolve();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: this.preorderAttrs })
    }).then(function (res) {
      if (!res.ok) console.warn('[kit-configurator] cart attributes update failed: ' + res.status);
      return true;
    }).catch(function (err) {
      console.warn('[kit-configurator] cart attributes update error', err);
      return true;
    });
  };

  // Isolated so the post-add destination is easy to change.
  KitConfigurator.prototype.redirectAfterAdd = function (action) {
    var dest = (action === 'checkout')
      ? '/checkout'
      : ((window.routes && window.routes.cart_url) || '/cart');

    // Host mode: route through Shopify's /discount/<code> permalink, which
    // applies the discount and forwards to the destination. This is what
    // auto-applies the host's 30% off. Public visitors skip this entirely.
    if (this.hostMode && this.hostCode) {
      window.location.href = '/discount/' + encodeURIComponent(this.hostCode) +
        '?redirect=' + encodeURIComponent(dest);
      return;
    }

    window.location.href = dest;
  };

  KitConfigurator.prototype.persist = function () {
    var self = this;
    var state = {
      items: this.items.map(function (it) {
        return {
          block_id: it.block_id,
          product_id: it.product_id,
          variant_id: self.selectedVariant(it).id,
          title: it.title,
          price_cents: it.price_cents,
          image_url: it.image_url,
          is_anchor: it.is_anchor,
          available: it.available,
          kept: it.is_anchor ? true : !!self.kept[it.block_id]
        };
      }),
      preview_collapsed: this.previewCollapsed
    };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (e) { /* storage may be unavailable; degrade silently */ }
  };

  KitConfigurator.prototype.restore = function () {
    var raw;
    try { raw = localStorage.getItem(this.storageKey); } catch (e) { return; }
    if (!raw) return;
    var saved;
    try { saved = JSON.parse(raw); } catch (e) { return; }
    if (!saved) return;

    this.previewCollapsed = !!saved.preview_collapsed;

    if (saved.items && saved.items.length) {
      var savedById = {};
      saved.items.forEach(function (s) { savedById[s.block_id] = s; });
      // Only restore kept-flags for blocks that still exist AND are still
      // available + still the same variant (guards against stale carts).
      this.items.forEach(function (it) {
        var s = savedById[it.block_id];
        if (!s) return;
        if (!it.available) { this.kept[it.block_id] = false; return; }
        if (String(s.variant_id) !== String(it.variant_id)) {
          // Not the default: honour it only if it is still one of this
          // item's purchasable variants (guards against product swaps).
          var pick = null;
          (it.variants || []).forEach(function (v) {
            if (String(v.id) === String(s.variant_id) && v.available) pick = v;
          });
          if (!pick) return;
          this.variant[it.block_id] = pick.id;
        }
        this.kept[it.block_id] = it.is_anchor ? true : !!s.kept;
      }, this);
    }
  };

  KitConfigurator.prototype.clearState = function () {
    try { localStorage.removeItem(this.storageKey); } catch (e) { /* no-op */ }
  };

  function init() {
    var roots = document.querySelectorAll('[data-kit-configurator]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__kitInit) return;
      root.__kitInit = true;
      new KitConfigurator(root);
    });
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }

  // Re-init when a section is re-rendered in the Theme Editor.
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target && e.target.querySelector ? e.target.querySelector('[data-kit-configurator]') : null;
    if (root && !root.__kitInit) {
      root.__kitInit = true;
      new KitConfigurator(root);
    }
  });
})();
